import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, Download } from "lucide-react";

const EXPERIMENT = ["One recurring meal", "One care exchange", "One shared resource", "One honest check-in"];

const QUESTIONS = [
  "Who is already helping raise your children?",
  "Who do your children trust?",
  "Who has been doing the work of kin without being named as kin?",
  "What could become possible if family were less about defending one structure and more about building enough relationship that none of us has to do this alone?",
];

const Header = () => (
  <div className="flex items-center justify-between gap-4 print:hidden">
    <Link to="/" className="font-mono fr-lbl inline-flex items-center gap-2 hover:text-rose transition-colors duration-200" data-testid="practice-back-link">
      <ArrowLeft size={16} /> Back to the book
    </Link>
    <div className="flex flex-wrap gap-3">
      <a href="/free-practice.pdf" download className="font-mono fr-lbl border-2 border-ink px-5 py-3 inline-flex items-center gap-2 hover:bg-ink hover:text-marigold transition-colors duration-200" data-testid="practice-download-pdf">
        <Download size={16} /> Download PDF
      </a>
      <button onClick={() => window.print()} className="fr-btn px-5 py-3 inline-flex items-center gap-2" data-testid="practice-print-button">
        <Printer size={16} /> Print
      </button>
    </div>
  </div>
);

const Experiment = () => (
  <section className="mt-12" data-testid="practice-experiment">
    <p className="font-mono fr-lbl">Practice 2 / From Chapter 11</p>
    <h2 className="font-mono font-bold fr-h2 mt-2">The ninety-day experiment</h2>
    <p className="mt-4 text-[17px] leading-[1.6] max-w-[60ch]">
      Before making large housing commitments, choose one recurring meal, one care exchange, one shared resource, and one honest check-in. Try them for ninety days.
    </p>
    <ol className="mt-8 grid sm:grid-cols-2 gap-4">
      {EXPERIMENT.map((t, i) => (
        <li key={t} className="border-4 border-ink bg-white p-6 break-inside-avoid" data-testid={`practice-step-${i + 1}`}>
          <p className="font-mono font-bold text-xl">[{i + 1}] {t}</p>
          <p className="font-mono fr-lbl mt-6">Mine is: ______________________</p>
        </li>
      ))}
    </ol>
  </section>
);

export default function Practice() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Free practice | Families, Reimagined";
  }, []);
  return (
    <div className="min-h-screen bg-sand text-ink font-body" data-testid="practice-page">
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-20">
        <Header />
        <p className="font-mono fr-lbl mt-12">Families, Reimagined / Zarya Thomas / Free practice</p>
        <h1 className="font-mono font-bold text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">Begin from where you already live.</h1>
        <section className="mt-12 bg-marigold border-4 border-ink p-8" data-testid="practice-questions">
          <p className="font-mono fr-lbl">Practice 1 / From the opening</p>
          <h2 className="font-mono font-bold fr-h2 mt-2">Who is already your family?</h2>
          <ul className="mt-6 space-y-4 text-[17px] leading-[1.6]">
            {QUESTIONS.map((q) => <li key={q} className="border-l-4 border-ink pl-4">{q}</li>)}
          </ul>
        </section>
        <Experiment />
        <p className="font-mono font-bold text-xl mt-16 max-w-[40ch]">The structure has changed. The commitment to not doing family alone has not.</p>
        <p className="mt-6 text-[15px] print:hidden">Families, Reimagined is coming 2027. Follow along at <a href="https://www.instagram.com/riseandshiny_/" target="_blank" rel="noopener noreferrer" className="underline hover:text-rose" data-testid="practice-instagram-link">@riseandshiny_</a>.</p>
      </div>
    </div>
  );
}
